import { useCallback, useMemo } from 'react';
import parse from 'html-react-parser';

import { generateLinkViewerUrl } from '@setvi/shared/utils/viewer';
import { VARIABLE_TYPE } from '@setvi/shared/enums';

import { SPreviewProps } from './index';
import { getPreviewLink } from '../../utils';

type UsePreviewProps = SPreviewProps;

// Decode HTML entities (e.g., &amp; -> &, &lt; -> <)
const decodeHtmlEntities = (html: string): string => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = html;
  return textarea.value;
};

// Extract plain text from HTML subject, preserving variable styling for preview
const processSubjectForPreview = (subject: string): string => {
  const div = document.createElement('div');
  div.innerHTML = subject;

  // Get text content (strips HTML tags)
  const textContent = div.textContent || div.innerText || '';

  // Decode any HTML entities and wrap variables with styled spans
  const decoded = decodeHtmlEntities(textContent);
  return decoded.replace(
    /\{\{([^}]*(?:First Name|Last Name|Company Name))\}\}/g,
    '<span class="mce-mergetag">{{$1}}</span>'
  );
};

// Extract plain text and replace variables with recipient values
const processSubjectWithValues = (
  subject: string,
  values: Record<string, string>
): string => {
  const div = document.createElement('div');
  div.innerHTML = subject;

  // Get text content (strips HTML tags)
  let textContent = div.textContent || div.innerText || '';

  // Decode HTML entities
  textContent = decodeHtmlEntities(textContent);

  // Replace variables with values
  return textContent.replace(
    /{{(.*?)}}/g,
    (match, placeholder) => values[placeholder.trim()] ?? match
  );
};

export const usePreview = ({
  emailSubject,
  emailBody,
  variables: selectedVariables,
  links,
  selectedRecipient,
  showRecipientValues
}: UsePreviewProps) => {
  const replaceVariables = useCallback(
    (body: string, variables: Array<VARIABLE_TYPE> = []) => {
      let result = body;

      for (let i = 0; i < variables.length; i += 1) {
        const values: { [key: string]: string } = {
          'First Name': selectedRecipient.FirstName,
          'Last Name': selectedRecipient.LastName,
          'Company Name': selectedRecipient.CompanyName
        };

        result = result.replace(
          /{{(.*?)}}/g,
          (match, placeholder) => values[placeholder.trim()] ?? match
        );
      }
      // Remove mce-mergetag span wrappers entirely, keeping inner content
      result = result.replace(
        /<span[^>]*class="mce-mergetag"[^>]*>(.*?)<\/span>/g,
        '$1'
      );

      return result;
    },
    [selectedRecipient]
  );

  const htmlPreviewSubject = useMemo(() => {
    if (showRecipientValues && selectedRecipient) {
      // Replace variables with actual recipient values
      const values: Record<string, string> = {
        'First Name': selectedRecipient.FirstName || '',
        'Last Name': selectedRecipient.LastName || '',
        'Company Name': selectedRecipient.CompanyName || ''
      };
      const processed = processSubjectWithValues(emailSubject, values);
      return <>{processed}</>;
    }
    // Show variables with styling for preview
    const processed = processSubjectForPreview(emailSubject);
    return <>{parse(processed)}</>;
  }, [emailSubject, selectedRecipient, showRecipientValues]);

  const processLinksInHtml = useCallback(
    (html: string) => {
      if (!links.length) return html;

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const anchors = doc.querySelectorAll('a[href]');

      anchors.forEach(anchor => {
        const href = anchor.getAttribute('href');
        const id = anchor.getAttribute('id');

        const matchingLink = links.find(
          link => link.Placeholder === href || link.Placeholder === id
        );

        if (matchingLink) {
          const previewLink = getPreviewLink(matchingLink);
          const viewerUrl = generateLinkViewerUrl(previewLink);
          anchor.setAttribute('data-preview', viewerUrl);
        }
      });

      return doc.body.innerHTML;
    },
    [links]
  );

  const htmlPreviewBody = useMemo(() => {
    const processedEmailBody = processLinksInHtml(emailBody);

    return showRecipientValues && selectedRecipient
      ? replaceVariables(processedEmailBody, selectedVariables)
      : processedEmailBody;
  }, [
    emailBody,
    processLinksInHtml,
    selectedVariables,
    replaceVariables,
    selectedRecipient,
    showRecipientValues
  ]);

  return {
    htmlPreviewSubject,
    htmlPreviewBody
  };
};
